import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Room, RoomEvent } from 'livekit-client';
import { API } from '../config';
import { tr } from '../til';

const btn = {
  padding: '10px 24px',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  cursor: 'pointer',
  color: '#fff'
};

const smallBtn = {
  padding: '6px 14px',
  background: '#eef1f7',
  color: '#1a1a2e',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '12px',
  cursor: 'pointer'
};

const karta = {
  background: '#fff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
  marginBottom: '20px'
};

/// Ishtirokchi ma'lumoti LiveKit metadata'sidan olinadi — server
/// o'zgarishlarni o'zi tarqatadi, qayta so'rov kerak emas.
const metadataniOqish = (p) => {
  let md = {};
  try {
    if (p.metadata) md = JSON.parse(p.metadata);
  } catch {
    // Buzuq metadata ishtirokchini ro'yxatdan chiqarib yubormasin
  }
  return {
    identity: p.identity,
    userId: md.user_id ?? null,
    ism: md.full_name || p.name || p.identity,
    unvon: md.rank || null,
    tuman: md.district_name || null,
    rahbarmi: md.is_host === true,
    qolKotarilgan: md.hand_raised === true,
    sozBerilgan: md.has_floor === true,
    gapiryapti: p.isSpeaking === true
  };
};

function Meetings() {
  const [meeting, setMeeting] = useState(null);
  const [tarix, setTarix] = useState([]);
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [ulangan, setUlangan] = useState(false);
  const [mikrofon, setMikrofon] = useState(false);
  const [ishtirokchilar, setIshtirokchilar] = useState([]);

  const roomRef = useRef(null);
  const audioRef = useRef(null);

  const token = localStorage.getItem('token');
  const auth = { headers: { authorization: token } };

  const load = useCallback(async () => {
    try {
      const [faol, hammasi] = await Promise.all([
        axios.get(`${API}/meetings/active`, auth),
        axios.get(`${API}/meetings`, auth)
      ]);
      setMeeting(faol.data.meeting);
      setTarix(hammasi.data);
    } catch (err) {
      setError(err.response?.data?.error || tr("Ma'lumot olinmadi"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Ekrandan chiqilganda ulanish albatta uzilsin — aks holda rahbar
  // boshqa sahifaga o'tsa ham mikrofoni ochiq qolardi.
  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, []);

  const ishtirokchilarniYangilash = useCallback((room) => {
    const royxat = [];
    if (room.localParticipant) royxat.push(metadataniOqish(room.localParticipant));
    room.remoteParticipants.forEach(p => royxat.push(metadataniOqish(p)));

    royxat.sort((a, b) => {
      if (a.rahbarmi !== b.rahbarmi) return a.rahbarmi ? -1 : 1;
      if (a.sozBerilgan !== b.sozBerilgan) return a.sozBerilgan ? -1 : 1;
      if (a.qolKotarilgan !== b.qolKotarilgan) return a.qolKotarilgan ? -1 : 1;
      return (a.tuman || a.ism).localeCompare(b.tuman || b.ism);
    });

    setIshtirokchilar(royxat);
  }, []);

  const ulanish = async (meetingId) => {
    setError('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/meetings/${meetingId}/join`, {}, auth);
      const { url, token: lkToken } = res.data;

      const room = new Room({ adaptiveStream: false, dynacast: false });
      roomRef.current = room;

      const yangila = () => ishtirokchilarniYangilash(room);

      room
        .on(RoomEvent.ParticipantConnected, yangila)
        .on(RoomEvent.ParticipantDisconnected, yangila)
        .on(RoomEvent.ParticipantMetadataChanged, yangila)
        .on(RoomEvent.ActiveSpeakersChanged, yangila)
        .on(RoomEvent.TrackMuted, yangila)
        .on(RoomEvent.TrackUnmuted, yangila)
        .on(RoomEvent.Disconnected, () => {
          setUlangan(false);
          setIshtirokchilar([]);
          roomRef.current = null;
        })
        // Boshqa ishtirokchining ovozi kelganda uni sahifaga ulaymiz,
        // aks holda brauzer hech narsa eshittirmaydi.
        .on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === 'audio' && audioRef.current) {
            audioRef.current.appendChild(track.attach());
          }
          yangila();
        })
        .on(RoomEvent.TrackUnsubscribed, (track) => {
          track.detach().forEach(el => el.remove());
          yangila();
        });

      await room.connect(url, lkToken);

      // Brauzerlar avtomatik ovoz ijrosini to'sadi — foydalanuvchi
      // tugma bosgani uchun shu yerda ruxsat so'ray olamiz.
      await room.startAudio().catch(() => {});

      await room.localParticipant.setMicrophoneEnabled(true);
      setMikrofon(true);
      setUlangan(true);
      yangila();
    } catch (err) {
      setError(err.response?.data?.error || tr("Ulanib bo'lmadi: {message}", { message: err.message }));
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    } finally {
      setBusy(false);
    }
  };

  const boshlash = async () => {
    setError('');
    setSuccess('');
    if (!title.trim()) { setError(tr("Yig'ilish nomini kiriting")); return; }

    setBusy(true);
    try {
      const res = await axios.post(`${API}/meetings`, { title }, auth);
      setSuccess(
        tr("Yig'ilish boshlandi. Bildirishnoma yuborildi: ") +
        tr("{xabar_yuborildi} / {xabar_kutilgan} xodimga.", { xabar_yuborildi: res.data.xabar_yuborildi, xabar_kutilgan: res.data.xabar_kutilgan })
      );
      setTitle('');
      setMeeting(res.data.meeting);
      await ulanish(res.data.meeting.id);
      load();
    } catch (err) {
      setError(err.response?.data?.error || tr("Yig'ilish boshlanmadi"));
    } finally {
      setBusy(false);
    }
  };

  const yakunlash = async () => {
    if (!window.confirm(tr("Yig'ilish yakunlansinmi? Barcha ishtirokchilar uziladi."))) return;
    setBusy(true);
    try {
      await axios.post(`${API}/meetings/${meeting.id}/end`, {}, auth);
      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }
      setUlangan(false);
      setMeeting(null);
      setIshtirokchilar([]);
      setSuccess(tr("Yig'ilish yakunlandi."));
      load();
    } catch (err) {
      setError(err.response?.data?.error || tr("Yakunlanmadi"));
    } finally {
      setBusy(false);
    }
  };

  const chiqish = async () => {
    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }
    await axios.post(`${API}/meetings/${meeting.id}/leave`, {}, auth).catch(() => {});
    setUlangan(false);
    setIshtirokchilar([]);
  };

  /// So'z berish — yig'ilishning asosiy mexanizmi.
  /// Mikrofon serverda ochiladi, ilovada emas.
  const sozBerish = async (i) => {
    setError('');
    try {
      const res = await axios.post(
        `${API}/meetings/${meeting.id}/floor`,
        { user_id: i.sozBerilgan ? null : i.userId },
        auth
      );
      setSuccess(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || tr("So'z berilmadi"));
    }
  };

  const mikrofonAlmashtirish = async () => {
    if (!roomRef.current) return;
    const yangi = !mikrofon;
    await roomRef.current.localParticipant.setMicrophoneEnabled(yangi);
    setMikrofon(yangi);
  };

  const qolKotarganlar = ishtirokchilar.filter(i => i.qolKotarilgan).length;

  return (
    <div>
      <h2 style={{ marginBottom: '8px', fontSize: '22px' }}>{tr("Ovozli yig'ilish")}</h2>
      <p style={{ marginTop: 0, marginBottom: '20px', color: '#888', fontSize: '13px' }}>
        {tr("Siz gapirasiz — tumanlar eshitadi. Biror tumandan javob olish uchun ro'yxatdan \"So'z berish\" tugmasini bosing. Bir vaqtda faqat bitta tuman gapira oladi.")}
      </p>

      {error && (
        <div style={{ background: '#ffeaea', color: '#c93636', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#e9f9ed', color: '#2d8a2d', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' }}>
          {success}
        </div>
      )}

      {/* Boshqa ishtirokchilarning ovozi shu yerga ulanadi */}
      <div ref={audioRef} style={{ display: 'none' }} />

      {!meeting ? (
        <div style={karta}>
          <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>{tr("Yangi yig'ilish")}</h3>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              placeholder={tr("Yig'ilish nomi * (masalan: Haftalik hisobot)")}
              value={title}
              maxLength={200}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && boshlash()}
              style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
            />
            <button
              onClick={boshlash}
              disabled={busy}
              style={{ ...btn, background: busy ? '#888' : '#2d8a2d', whiteSpace: 'nowrap' }}
            >
              {busy ? tr("Boshlanmoqda...") : tr("🎙️ Boshlash")}
            </button>
          </div>
          <p style={{ margin: '14px 0 0', color: '#888', fontSize: '12px' }}>
            {tr("Boshlash bilan barcha tumanga biriktirilgan xodimlarga telefoniga bildirishnoma yuboriladi.")}
          </p>
        </div>
      ) : (
        <div style={{ ...karta, borderLeft: '4px solid #c93636' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ background: '#c93636', color: '#fff', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
                  ● {tr("JONLI")}
                </span>
                <h3 style={{ margin: 0, fontSize: '17px' }}>{meeting.title}</h3>
              </div>
              <p style={{ margin: '6px 0 0', color: '#888', fontSize: '13px' }}>
                {tr("Boshlangan: {toLocaleString}", { toLocaleString: new Date(meeting.started_at).toLocaleString('uz-UZ') })}
                {qolKotarganlar > 0 && (
                  <span style={{ color: '#e67e22', marginLeft: '12px' }}>
                    ✋ {tr("{qolKotarganlar} ta savol", { qolKotarganlar })}
                  </span>
                )}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {!ulangan ? (
                <button
                  onClick={() => ulanish(meeting.id)}
                  disabled={busy}
                  style={{ ...btn, background: busy ? '#888' : '#2d8a2d' }}
                >
                  {busy ? tr("Ulanmoqda...") : tr("🎧 Ulanish")}
                </button>
              ) : (
                <>
                  <button
                    onClick={mikrofonAlmashtirish}
                    style={{ ...btn, background: mikrofon ? '#2d8a2d' : '#888' }}
                  >
                    {mikrofon ? tr("🎙️ Mikrofon ochiq") : tr("🔇 Jim")}
                  </button>
                  <button onClick={chiqish} style={{ ...btn, background: '#888' }}>
                    {tr("Chiqish")}
                  </button>
                </>
              )}
              <button onClick={yakunlash} disabled={busy} style={{ ...btn, background: '#c93636' }}>
                {tr("Yakunlash")}
              </button>
            </div>
          </div>

          {ulangan && (
            <div style={{ marginTop: '22px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>
                {tr("Ishtirokchilar ({soni})", { soni: ishtirokchilar.length })}
              </h4>

              {ishtirokchilar.length === 0 && (
                <p style={{ color: '#999', fontSize: '13px' }}>{tr("Hali hech kim qo'shilmadi.")}</p>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <tbody>
                  {ishtirokchilar.map(i => (
                    <tr
                      key={i.identity}
                      style={{
                        borderBottom: '1px solid #f5f5f5',
                        background: i.gapiryapti ? '#f0fbf3' : i.qolKotarilgan ? '#fff8ef' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '10px 8px', width: '40px' }}>
                        {i.rahbarmi ? '👑' : i.qolKotarilgan ? '✋' : i.sozBerilgan ? '🎙️' : '🎧'}
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <div style={{ fontWeight: '500' }}>
                          {i.tuman || i.ism}
                          {i.gapiryapti && (
                            <span style={{ color: '#2d8a2d', fontSize: '12px', marginLeft: '8px' }}>
                              {tr("gapirmoqda...")}
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#999', fontSize: '12px' }}>
                          {i.unvon ? `${i.unvon} ` : ''}{i.ism}
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {i.rahbarmi ? (
                          <span style={{ color: '#999', fontSize: '12px' }}>{tr("siz")}</span>
                        ) : i.sozBerilgan ? (
                          <button
                            onClick={() => sozBerish(i)}
                            style={{ ...smallBtn, background: '#c93636', color: '#fff', border: 'none' }}
                          >
                            {tr("So'zni qaytarish")}
                          </button>
                        ) : (
                          <button
                            onClick={() => sozBerish(i)}
                            style={{ ...smallBtn, background: '#1a1a2e', color: '#fff', border: 'none' }}
                          >
                            {tr("So'z berish")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!ulangan && (
            <p style={{ margin: '16px 0 0', color: '#888', fontSize: '13px' }}>
              {tr("Yig'ilish davom etmoqda, lekin siz ulanmagansiz. Gapirish va so'z berish uchun \"Ulanish\" tugmasini bosing.")}
            </p>
          )}
        </div>
      )}

      <div style={karta}>
        <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>{tr("O'tgan yig'ilishlar")}</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Nomi")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Boshlovchi")}</th>
              <th style={{ textAlign: 'left', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Sana")}</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Davomiylik")}</th>
              <th style={{ textAlign: 'center', padding: '10px 8px', color: '#888', fontWeight: '500' }}>{tr("Ishtirokchi")}</th>
            </tr>
          </thead>
          <tbody>
            {tarix.map(m => (
              <tr key={m.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                <td style={{ padding: '10px 8px', fontWeight: '500' }}>
                  {m.title}
                  {m.status === 'jonli' && (
                    <span style={{ color: '#c93636', fontSize: '11px', marginLeft: '8px' }}>● {tr("jonli")}</span>
                  )}
                </td>
                <td style={{ padding: '10px 8px', color: '#666' }}>
                  {m.host_rank ? `${m.host_rank} ` : ''}{m.host_name}
                </td>
                <td style={{ padding: '10px 8px', color: '#888', fontSize: '13px' }}>
                  {new Date(m.started_at).toLocaleString('uz-UZ')}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'center', color: '#555' }}>
                  {tr("{round} daq", { round: Math.round((m.davomiyligi_sekund || 0) / 60) })}
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'center', color: '#555' }}>
                  {m.ishtirokchilar_soni}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {tarix.length === 0 && (
          <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: '20px 0 0' }}>
            {tr("Hali yig'ilish o'tkazilmagan.")}
          </p>
        )}
      </div>
    </div>
  );
}

export default Meetings;
